import type { CustomerOrganizationType } from './dto/customer.js';

/**
 * SIL-1034: org-feature gating primitive, replacing the SIL-1019
 * `business: true` boolean axis (middleware/clientAccess.ts) and the
 * customer-portal `businessOnly`/`individualTo` nav axis (customerNav.ts)
 * with a single named-feature axis shared by both. `hasOrgFeature` is the
 * ONLY function that decides whether an org type unlocks a feature — every
 * API gate and every customer-portal nav/route decision must call it (or
 * `useOrgFeature`/`useOrgFeatureState`, which call it), never re-derive the
 * business/individual distinction inline.
 */
export type OrgType = CustomerOrganizationType; // 'business' | 'individual'

/**
 * Feature -> org types that unlock it. Every feature but `manualSerialEntry`
 * maps to exactly `['business']` — this is not a coincidence to special-case away;
 * it is the entire reason this map exists instead of a boolean. A future
 * org type (e.g. 'partner') gets its own row here, not a change to every
 * call site.
 */
const ORG_FEATURES_DEF = {
  teams: ['business'],
  locations: ['business'],
  orgProfile: ['business'], // business /profile, logo, PUE
  finance: ['business'], // financials/*, ERP import triage (only triage gated today)
  scenarios: ['business'], // incl. approval setting, templates, per-scenario ESG exports
  sustainability: ['business'], // carbon targets/trajectory, environmental/*; carbon.csv = SIL-1035
  fleetData: ['business'], // integrations, Datadog, BMC, Prometheus, data health
  verificationLinks: ['business'], // org-wide link + page (per-asset links stay open)
  onboarding: ['business'], // bulk-CSV onboarding wizard
  bulkImport: ['business'], // no consumer yet (spec step 2)
  lots: ['business'],
  marketplaceOps: ['business'], // buyer demand, matches, market inventory, passports registry
  custody: ['business'],
  marketIntel: ['business'], // Market Intelligence purchase-decision section (SIL-1070 forecast band, SIL-1071 house rates)
  dataProduct: ['business'], // SAPI data product: data_subscription panel + (P1-B onward) API keys, SIL-2055
  // The ONE individual-only feature: one-time manual serial entry after a
  // TrueRead probe (929). A business org has an ERP / CSV path to a serial.
  manualSerialEntry: ['individual'],
} as const satisfies Record<string, readonly OrgType[]>;

export type OrgFeature = keyof typeof ORG_FEATURES_DEF;

function deepFreeze<T>(value: T): T {
  Object.freeze(value);
  if (value && typeof value === 'object') {
    for (const key of Object.keys(value as object)) {
      const v = (value as Record<string, unknown>)[key];
      if (v && typeof v === 'object' && !Object.isFrozen(v)) deepFreeze(v);
    }
  }
  return value;
}

export const ORG_FEATURES: { readonly [F in OrgFeature]: readonly OrgType[] } =
  deepFreeze(ORG_FEATURES_DEF);

export const ORG_FEATURE_NAMES = Object.keys(ORG_FEATURES) as readonly OrgFeature[];

/** Key-membership check only — does NOT decide access. Use `hasOrgFeature` for that. */
export function isOrgFeature(v: unknown): v is OrgFeature {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(ORG_FEATURES, v);
}

/**
 * The single access decision for a (org type, feature) pair. Fail-closed:
 * `null`/`undefined` org type and any unrecognized feature string both deny.
 * `Object.prototype.hasOwnProperty.call` guards prototype-pollution keys
 * like `'__proto__'`/`'toString'` (O2, security review) and this is shared
 * code that ships to the browser, so it avoids `Object.hasOwn` for wider
 * runtime compat.
 */
export function hasOrgFeature(
  orgType: OrgType | null | undefined,
  feature: OrgFeature,
): boolean {
  if (orgType == null) return false;
  if (!Object.prototype.hasOwnProperty.call(ORG_FEATURES, feature)) return false;
  return (ORG_FEATURES[feature] as readonly string[]).includes(orgType);
}
